import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { TableProperties } from "../../../shared/components/ui/Table/types";
import {
  selectIsUpdateIpAccessRules,
  selectIpAccessRulesForUpdate,
  selectIpAccessRulesRefreshTable,
} from "../../../redux";
import {
  refreshTableIpAccessRules,
  setIsIpAccessRulesForUpdate,
  ipAccessRulesChangeModalState,
  createIpAccessRules,
  updateIpAccessRules,
} from "../../../redux/actions/ipAccessRules.actions";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { DataIpAccessRules } from "../../../redux/@types";
import { message } from "antd";
import {
  validateCreateIpAccessRules,
  validateUpdateIpAccessRules,
} from "../../../helpers";

export const useIpAccessRules = () => {
  const dispatch = useAppDispatch();

  const isUpdateIpAccessRules = useSelector(selectIsUpdateIpAccessRules);
  const dataUpdateIpAccessRules = useSelector(selectIpAccessRulesForUpdate);
  const isRefreshTableIpAccessRules = useSelector(
    selectIpAccessRulesRefreshTable
  );

  const [isLoading, setIsLoading] = useState(false);
  const [tableProps, setTableProps] = useState<TableProperties>();

  const changeModalIpAccessRulesState = () => {
    formMethodsCreate.reset({
      id: 0,
      ip: "",
      type: "block",
      reason: "",
      status: "active",
    });
    dispatch(setIsIpAccessRulesForUpdate(false));
    dispatch(ipAccessRulesChangeModalState());
  };

  const openCreateWithIp = (ip: string, type: "block" | "allow" = "block") => {
    formMethodsCreate.reset({
      id: 0,
      ip,
      type,
      reason: "",
      status: "active",
    });
    dispatch(setIsIpAccessRulesForUpdate(false));
    dispatch(ipAccessRulesChangeModalState());
  };

  const formMethodsCreate = useForm<DataIpAccessRules>({
    resolver: yupResolver(validateCreateIpAccessRules) as any,
    defaultValues: {
      id: 0,
      ip: "",
      type: "block",
      reason: "",
      status: "active",
    },
  });

  const formMethodsUpdate = useForm<DataIpAccessRules>({
    resolver: yupResolver(validateUpdateIpAccessRules) as any,
  });

  useEffect(() => {
    if (isUpdateIpAccessRules) {
      formMethodsCreate.reset();
      formMethodsUpdate.reset({ ...dataUpdateIpAccessRules });
    }

    return () => {
      formMethodsUpdate.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateIpAccessRules, dataUpdateIpAccessRules]);

  const onSubmitCreateOrUpdate = async (data: DataIpAccessRules) => {
    if (isLoading) return;
    setIsLoading(true);
    if (isUpdateIpAccessRules) {
      await updateIpAccessRules({
        id: data.id,
        ip: data.ip,
        type: data.type,
        reason: data.reason,
        status: data.status || "active",
      })
        .then((res: Partial<DataIpAccessRules>) => {
          setIsLoading(false);
          if (res.id) {
            message.success(`Regla de IP actualizada (id: ${res.id})`);
            dispatch(refreshTableIpAccessRules(true));
            dispatch(ipAccessRulesChangeModalState());
          } else {
            message.error(res as string);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          message.error(err.message);
        });
    } else {
      await createIpAccessRules({
        ip: data.ip,
        type: data.type,
        reason: data.reason,
        status: data.status || "active",
        source: "manual",
      })
        .then((res: DataIpAccessRules) => {
          setIsLoading(false);
          if (res.id) {
            dispatch(ipAccessRulesChangeModalState());
            message.success(`Regla de IP creada (id: ${res.id})`);
            dispatch(refreshTableIpAccessRules(true));
          } else {
            message.error(res as any);
          }
        })
        .catch((err: any) => {
          setIsLoading(false);
          message.error(err.message);
        });
    }
  };

  const deactivateRule = async (id: number) => {
    await updateIpAccessRules({ id, status: "inactive" })
      .then(() => {
        message.success("IP desbloqueada (regla desactivada)");
        dispatch(refreshTableIpAccessRules(true));
      })
      .catch((err: any) => message.error(err.message));
  };

  useEffect(() => {
    if (isRefreshTableIpAccessRules) {
      tableProps?.refresh();
      dispatch(refreshTableIpAccessRules(false));
    }
  }, [isRefreshTableIpAccessRules]);

  return {
    isUpdateIpAccessRules,
    isLoading,
    tableProps,
    formMethodsCreate,
    formMethodsUpdate,
    setTableProps,
    onSubmitCreateOrUpdate,
    changeModalIpAccessRulesState,
    openCreateWithIp,
    deactivateRule,
  };
};
